import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { combineLatest, of, Subscription } from 'rxjs';
import {
  ChannelConnection,
  ChannelType,
  EventBusService,
} from '@onep/front-common-lib/event-bus';
import {
  AuthInfo,
  AuthInfoType,
  CustomizationData,
} from '@onep/front-portal-lib';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  AdminManagementService,
  IS_ADMIN_MANAGEMENT_SERVICE,
} from './admin/admin-management.service';
import { SharedInfoTrackingService } from './tracking/shared-info-tracking.service';
import { CustomizationDataService } from './auth/customization-data/customization-data.service';
import { environment } from '../environments/environment';
import { GetAuthenticatedUserLocaleGQL } from '../generated/types';
import { frontCommonLanguages } from '@onep/front-common-lib/common';

@Component({
  selector: 'shared-info-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private allSubscriptions = new Subscription();

  private channelConnection!: ChannelConnection<AuthInfo>;

  private logoutChannel!: ChannelConnection<boolean>;

  private languageChannel!: ChannelConnection<string>;

  private customizationDataChannel!: ChannelConnection<CustomizationData>;

  private managerBridgeOrgIdChannel!: ChannelConnection<boolean>;

  constructor(
    private publicEventsService: PublicEventsService,
    private securityService: OidcSecurityService,
    private eventBusService: EventBusService,
    @Inject(IS_ADMIN_MANAGEMENT_SERVICE)
    private adminManagementService: AdminManagementService,
    private sharedInfoTrackingService: SharedInfoTrackingService,
    private customizationDataService: CustomizationDataService,
    private getAuthenticatedUserLocaleGQL: GetAuthenticatedUserLocaleGQL
  ) {}

  ngOnInit(): void {
    this.connectToChannels();
    this.allSubscriptions.add(this.listenAndEmitToLogoutChannel());
    this.allSubscriptions.add(this.listenAndEmitToAuthChannel());
    this.allSubscriptions.add(this.listenAndEmitToCustomizationDataChannel());
    this.allSubscriptions.add(this.listenAndEmitUserInfoEvents());
    this.getAndEmitToLocaleChannel();
    this.getAndEmitIsManagerBridge();

    this.sharedInfoTrackingService.initTracking();
  }

  private connectToChannels(): void {
    this.logoutChannel = this.eventBusService.connectToChannel(
      'shared-info',
      'logout',
      { type: ChannelType.event }
    );

    this.channelConnection = this.eventBusService.connectToChannel(
      'shared-info',
      'auth',
      { type: ChannelType.replayLastValue }
    );

    this.languageChannel = this.eventBusService.connectToChannel(
      'shared-info',
      'locale',
      { type: ChannelType.replayLastValue }
    );

    this.customizationDataChannel = this.eventBusService.connectToChannel(
      'shared-info',
      'customization-data',
      { type: ChannelType.replayLastValue }
    );

    this.managerBridgeOrgIdChannel = this.eventBusService.connectToChannel(
      'shared-info',
      'manager-bridge',
      { type: ChannelType.replayLastValue }
    );
  }

  private listenAndEmitToAuthChannel(): Subscription {
    return combineLatest([
      this.publicEventsService.registerForEvents().pipe(
        filter((event) => {
          return (
            event.type === EventTypes.NewAuthorizationResult ||
            event.type === EventTypes.UserDataChanged
          );
        }),
        map(() => this.securityService.getToken()),
        distinctUntilChanged()
      ),
      this.securityService.isAuthenticated$.pipe(distinctUntilChanged()),
    ]).subscribe(([accessToken, isAuth]) => {
      if (isAuth && accessToken != null && accessToken.length > 0) {
        this.adminManagementService
          .getUserAdminRights()
          .subscribe((uiRights) => {
            if (
              !environment.features.admin ||
              (environment.features.admin && uiRights.canAccessAdmin)
            ) {
              this.channelConnection.emitter.next({
                type: AuthInfoType.AUTHENTICATED,
                accessToken,
              });
              this.eventBusService
                .connectToChannel('shared-info', 'admin-ui-rights', {
                  type: ChannelType.replayLastValue,
                })
                .emitter.next(uiRights);
              if (environment.personalizationRedirection) {
                this.customizationDataService.redirectToPerso();
              }
            }
          });
      } else {
        this.channelConnection.emitter.next({
          type: AuthInfoType.NOT_AUTHENTICATED,
        });
      }
    });
  }

  private listenAndEmitToLogoutChannel(): Subscription {
    return this.logoutChannel.receiver
      .pipe(
        switchMap((revokeTokens) => {
          if (revokeTokens) {
            return this.securityService.logoffAndRevokeTokens();
          } else {
            return of(false);
          }
        }),
        catchError((err) => {
          console.error('Unable to revoke token', err);
          return of(null);
        })
      )
      .subscribe(() => {
        this.securityService.authorize();
      });
  }

  private listenAndEmitToCustomizationDataChannel(): Subscription {
    return this.customizationDataService
      .getCustomizationData()
      .subscribe((response) => {
        if (response) {
          this.customizationDataChannel.emitter.next({
            primaryColor: response?.primaryColor,
            secondaryColor: response?.secondaryColor,
            logoUrl: response?.logoUrl,
            frontUrl: response?.frontUrl,
            font: response?.font,
          } as CustomizationData);
        }
      });
  }

  private listenAndEmitUserInfoEvents(): Subscription {
    return combineLatest([
      this.securityService.isAuthenticated$,
      this.securityService.userData$,
    ])
      .pipe(
        filter(([isAuth, userData]) => isAuth && userData !== null),
        take(1)
      )
      .subscribe(() => this.sharedInfoTrackingService.sendUserTrackingInfo());
  }

  private getAndEmitToLocaleChannel(): void {
    this.securityService.isAuthenticated$
      .pipe(
        switchMap((isAuth: boolean) => {
          if (isAuth) {
            return this.getAuthenticatedUserLocaleGQL.fetch();
          } else {
            return of(null);
          }
        })
      )
      .subscribe((res) => {
        if (res) {
          let locale = res.data.authenticatedUser.locale;
          if (locale) {
            const knownLocale =
              frontCommonLanguages.filter((t) => t.value === locale).length ===
              1;
            if (!knownLocale) {
              locale = 'fr_FR'; // FALLBACK
            }
          } else {
            locale = 'fr_FR'; // FALLBACK
          }
          this.languageChannel.emitter.next(locale);
        }
      });
  }

  private getAndEmitIsManagerBridge(): void {
    if (environment.features.organizationAsAdmin) {
      if (environment.organizationAsAdmin != null) {
        this.managerBridgeOrgIdChannel.emitter.next(
          window.location.host.match(
            environment.organizationAsAdmin.subDomainRegex
          ) != null
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.allSubscriptions?.unsubscribe();
  }
}
